// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/**
 * The `event_type` a pack's deadline row declares to say "this framework has
 * a credit-information supply register". Generic on purpose: any instrument
 * that makes consent-by-recipient-class load-bearing can declare it.
 */
export const CREDIT_INFORMATION_SUPPLY_EVENT_TYPE = 'credit_information_supply_lifecycle';
/** `register_surface` values pack forms declare to feed this register's vocabularies. */
export const CONSENT_EVIDENCE_REGISTER_SURFACE = 'consent_evidence';
export const CREDIT_INFORMATION_SUPPLY_REGISTER_SURFACE = 'credit_information_supply';
/** The form field ids whose `options[]` the vocabulary reader collects. */
export const CONSENT_EVIDENCE_VOCABULARY_FIELDS = {
    recipientClasses: 'recipient_classes_named',
    consentFormats: 'consent_channel',
};
export const CREDIT_INFORMATION_SUPPLY_VOCABULARY_FIELDS = {
    recipientClass: 'recipient_class',
    supplyChannels: 'supply_channel',
};
/** The boundary tag this register reads (a member of `BOUNDARY_GUIDANCE_TAGS`'s family). */
export const CREDIT_INFORMATION_SUPPLY_BOUNDARY_TAG = 'credit_information_supply_boundary';
/**
 * The option-level flag a pack sets on a recipient-class option to say "a
 * supply to this class needs a transfer review beside the consent" (AC5).
 * Read strictly: only the boolean `true` flags a class. Which classes carry
 * it is the pack's statement, never the platform's (R-46).
 */
export const PACK_OPTION_TRANSFER_REVIEW_FLAG = 'requires_transfer_review';
/**
 * Sprint 62.25.0 AC14 (idea-catalog #304) — whether the artefact an entry names
 * is still held here, re-checked at read.
 *
 * The write check (`assertArtefactExists`) proves the file exists when the entry
 * is saved. Nothing re-checked it afterwards, so an entry whose file was later
 * soft-deleted still read as *held here* — the page derived that from
 * `artefact_attachment_id` being non-null alone. The read now applies the SAME
 * predicate as the write (org, file id, not deleted, owner type, owner id):
 *
 * - `held_here` — the file the entry names is still held under the entry's owner.
 * - `no_longer_held` — the entry names a file, and that file is no longer held
 *   (deleted, or no longer owned by the entry's workflow).
 *
 * Read-time, never stored: a read never rewrites the entry.
 */
export const CONSENT_EVIDENCE_ARTEFACT_STATES = ['held_here', 'no_longer_held'];
// ── Supply entry (Slice B) ──
/**
 * Whether a supply rests on a consent entry. Stated, never inferred from a
 * null id — "the read failed" and "no entry exists" are different sentences
 * (Sprint 61.0.11), and a supply that happened without cover is the row the
 * register exists to show.
 */
export const CONSENT_COVER_STATES = ['covered', 'no_entry_found', 'withdrawn'];
/** Whether a transfer review was attached where the recipient class asks for one. */
export const TRANSFER_REVIEW_STATES = ['not_required', 'attached', 'missing'];
/**
 * Whether the row is closed, and if not, by what (AC5). `covered` only when
 * cover is `covered` AND the transfer review is `not_required` or `attached`;
 * consent alone never closes a supply to a class the pack flags.
 */
export const SUPPLY_CLOSURE_STATES = [
    'covered',
    'no_consent_entry',
    'consent_withdrawn',
    'transfer_review_missing',
];
// ── Collections ──
export const CONSENT_EVIDENCE_COLLECTION = 'grc_consent_evidence_entries';
export const CREDIT_INFORMATION_SUPPLIES_COLLECTION = 'grc_credit_information_supplies';
// ── Error codes ──
export const CREDIT_INFORMATION_SUPPLY_ERROR_CODES = {
    /** A consent entry naming no recipient class cannot be saved. */
    RECIPIENT_CLASS_REQUIRED: 'RECIPIENT_CLASS_REQUIRED',
    /** A value outside the installed pack vocabulary. */
    VOCABULARY_VALUE_UNKNOWN: 'VOCABULARY_VALUE_UNKNOWN',
    /** An artefact must be held here or referenced in custody — one of the two, not neither. */
    ARTEFACT_OR_CUSTODY_REQUIRED: 'ARTEFACT_OR_CUSTODY_REQUIRED',
    /** The consent entry named on a supply does not cover the supply's recipient class. */
    CONSENT_ENTRY_CLASS_MISMATCH: 'CONSENT_ENTRY_CLASS_MISMATCH',
    /**
     * No installed pack declares this register. Not `NO_…`: the R-46 lint reads
     * a jurisdiction-prefixed SCREAMING_SNAKE literal as a framework code, and
     * `NO` is one.
     */
    DECLARING_FRAMEWORK_MISSING: 'DECLARING_FRAMEWORK_MISSING',
};
//# sourceMappingURL=credit-information-supply.js.map